// /src/auth/AuthProvider.jsx
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";

// ⬇⬇⬇ use RELATIVE import ⬇⬇⬇
import { auth, db, ensureAuthPersistence } from "../lib/firebase";

import { doc, getDoc, serverTimestamp, setDoc, collection, addDoc } from "firebase/firestore";

// Send verification email via admin API
async function sendVerificationEmail(email, displayName, token) {
  const apiUrl = process.env.REACT_APP_EMAIL_API_URL || 'https://app.ridewatch.org/api';

  try {
    const response = await fetch(`${apiUrl}/send-verification-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        displayName,
        verificationToken: token,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to send verification email');
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw error;
  }
}

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function ensureUserDoc(u) {
    if (!u) {
      setProfile(null);
      return;
    }

    // Check IMMEDIATELY if this user is on the accept-invite page (driver registration)
    // If so, don't auto-create - let AcceptInvitePage handle it completely
    const isAcceptingInvite = window.location.pathname.includes('accept-invite');
    if (isAcceptingInvite) {
      console.log('User is accepting invite, skipping auto-creation of bus_company profile');
      setProfile(null);
      return;
    }

    // Partner pages have their own auth provider (PartnerAuthProvider).
    // Don't auto-create a bus_company profile while a partner is registering or signing in,
    // otherwise we race PartnerAuthProvider.register() and overwrite the sales_partner doc.
    if (window.location.pathname.startsWith('/partner')) {
      console.log('User is on a partner page, skipping auto-creation of bus_company profile');
      setProfile(null);
      return;
    }

    const ref = doc(db, "users", u.uid);

    // Wait a moment for any concurrent writes to complete
    await new Promise(resolve => setTimeout(resolve, 500));

    const snap = await getDoc(ref);
    if (!snap.exists()) {

      // New user (not from invite) - create user doc and auto-create company
      const companyName = u.displayName ? `${u.displayName}'s Bus Company` : "My Bus Company";

      // 1) Create company
      const companyRef = await addDoc(collection(db, "companies"), {
        name: companyName,
        address: "",
        description: "School bus transportation services",
        contact_person: u.displayName || "",
        contact_phone: "",
        owner_uid: u.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: "active",
      });

      // 2) Create user doc with company_id
      await setDoc(ref, {
        uid: u.uid,
        email: u.email,
        displayName: u.displayName,
        photoURL: u.photoURL || "",
        account_type: "bus_company",
        company_id: companyRef.id, // Auto-assign company
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setProfile((await getDoc(ref)).data());
    } else {
      // Existing user - check account type and email verification
      const profileData = snap.data();
      const accountType = profileData.account_type || 'user';

      // Block drivers and regular users from admin dashboard
      if (accountType === 'driver') {
        await auth.signOut();
        setProfile(null);
        throw new Error('Driver accounts cannot access the admin dashboard. Please use the Ride Watch Driver app.');
      }

      if (accountType === 'user') {
        await auth.signOut();
        setProfile(null);
        throw new Error('User accounts cannot access the admin dashboard. Please use the Ride Watch app.');
      }

      // Block sales partners from the admin dashboard (they have their own portal).
      // Without this, the auto-company-creation below would attach a bus company to a partner.
      if (accountType === 'sales_partner') {
        await auth.signOut();
        setProfile(null);
        throw new Error('Partner accounts cannot access the admin dashboard. Please sign in at the Partner portal.');
      }

      // Block unverified users (except drivers who verified via email invitation)
      if (profileData.emailVerified === false) {
        await auth.signOut();
        setProfile(null);
        throw new Error('Please verify your email address before signing in. Check your inbox for the verification link.');
      }

      // Skip company creation for super_admin users
      if (profileData.role === 'super_admin') {
        setProfile(profileData);
      } else if (!profileData.company_id) {
        // User exists but has no company - create one
        const companyName = u.displayName ? `${u.displayName}'s Bus Company` : "My Bus Company";

        const companyRef = await addDoc(collection(db, "companies"), {
          name: companyName,
          address: "",
          description: "School bus transportation services",
          contact_person: u.displayName || "",
          contact_phone: "",
          owner_uid: u.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: "active",
        });

        await setDoc(ref, { company_id: companyRef.id, updatedAt: serverTimestamp() }, { merge: true });
        setProfile((await getDoc(ref)).data());
      } else {
        setProfile(profileData);
      }
    }
  }

  useEffect(() => {
    // Set persistence once on mount (no top-level await needed)
    ensureAuthPersistence();

    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      try {
        await ensureUserDoc(u);
      } catch (e) {
        // Never let a profile error hang the app on the loading screen
        console.error('Failed to load user profile:', e);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Function to refresh profile manually
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const ref = doc(db, "users", user.uid);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      setProfile(snap.data());
    }
  }, [user]);

  const value = useMemo(() => ({
    user,
    profile,
    loading,
    refreshProfile,
    login: (email, password) => signInWithEmailAndPassword(auth, email, password),
    register: async ({ email, password, displayName, referralCode }) => {
      // Generate 6-digit verification code
      const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
      const verificationExpiry = new Date();
      verificationExpiry.setMinutes(verificationExpiry.getMinutes() + 15); // 15 minute expiry

      // Store registration data and verification code in Firestore
      const verificationRef = doc(collection(db, "verification_codes"));
      await setDoc(verificationRef, {
        email: email.toLowerCase().trim(),
        password: password, // Temporarily store password
        displayName: displayName || "",
        referralCode: referralCode || null, // Store referral code for later
        verificationCode: verificationCode,
        expiresAt: verificationExpiry,
        verified: false,
        createdAt: serverTimestamp(),
      });

      // Send verification email with code
      await sendVerificationEmail(email, displayName || email, verificationCode);

      return { verificationId: verificationRef.id, email };
    },
    completeRegistration: async (verificationId, code) => {
      // Get verification doc
      const verificationRef = doc(db, "verification_codes", verificationId);
      const verificationSnap = await getDoc(verificationRef);

      if (!verificationSnap.exists()) {
        throw new Error('Invalid verification code');
      }

      const data = verificationSnap.data();

      // Check if code matches
      if (data.verificationCode !== code) {
        throw new Error('Invalid verification code');
      }

      // Check if expired
      if (data.expiresAt.toDate() < new Date()) {
        throw new Error('Verification code expired');
      }

      // Check if already verified
      if (data.verified) {
        throw new Error('Verification code already used');
      }

      // Create Firebase account NOW
      const cred = await createUserWithEmailAndPassword(auth, data.email, data.password);
      if (data.displayName) await updateProfile(cred.user, { displayName: data.displayName });

      const userRef = doc(db, "users", cred.user.uid);
      const companyName = data.displayName ? `${data.displayName}'s Bus Company` : "My Bus Company";

      // Create company (include referral code if provided)
      const companyData = {
        name: companyName,
        address: "",
        description: "School bus transportation services",
        contact_person: data.displayName || "",
        contact_phone: "",
        owner_uid: cred.user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: "active",
      };

      // Add referral code if provided - this links the company to the sales partner
      if (data.referralCode) {
        companyData.referral_code_used = data.referralCode;
      }

      const companyRef = await addDoc(collection(db, "companies"), companyData);

      // Create user doc (include referral code if provided)
      const userDocData = {
        uid: cred.user.uid,
        email: cred.user.email,
        displayName: data.displayName || "",
        photoURL: cred.user.photoURL || "",
        account_type: "bus_company",
        company_id: companyRef.id,
        emailVerified: true, // Already verified via code
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Add referral code if provided
      if (data.referralCode) {
        userDocData.referred_by_code = data.referralCode;
      }

      await setDoc(userRef, userDocData);

      // Mark verification as used
      await setDoc(verificationRef, { verified: true }, { merge: true });

      // Delete password from verification doc for security
      await setDoc(verificationRef, { password: null }, { merge: true });

      return cred.user;
    },
    logout: () => signOut(auth),
    resetPassword: async (email) => {
      const apiUrl = process.env.REACT_APP_EMAIL_API_URL || 'https://app.ridewatch.org/api';
      const response = await fetch(`${apiUrl}/send-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send password reset email');
      }

      return response.json();
    },
  }), [user, profile, loading, refreshProfile]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
